import React, { createContext, useContext, useState, useEffect } from 'react';

interface CartContextType {
  quantity: number;
  setQuantity: (quantity: number) => void;
  openEmbedModal: () => void;
}

const CartContext = createContext<CartContextType>({
  quantity: 1,
  setQuantity: () => { },
  openEmbedModal: () => { },
});

export const useCart = () => useContext(CartContext);

interface CartProviderProps {
  children: React.ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [quantity, setQuantity] = useState(100); // Default to minimum 100 minutes

  // Function to handle scroll locking
  const updateScrollLock = () => {
    const modalElement = document.getElementById('embed-sdk-modal');
    const isModalVisible = modalElement && window.getComputedStyle(modalElement).display !== 'none';

    if (isModalVisible) {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${window.innerWidth - document.documentElement.clientWidth}px`;
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
  };

  // Function to open the WinMax Affiliates embed
  const openEmbedModal = () => {
    if (window.EmbedSDK) {
      const sdk = new window.EmbedSDK.initSDK({
        key: import.meta.env.VITE_WINMAX_EMBED_KEY
      });
      sdk.open(import.meta.env.VITE_WINMAX_PRODUCT_ID, quantity);
    } else {
      console.error('EmbedSDK not loaded');
    }
  };

  // Set up mutation observer to watch for modal changes
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          updateScrollLock();
        }
      }
    });

    // Start observing the document for modal changes
    const startObserver = () => {
      const modalElement = document.getElementById('embed-sdk-modal');
      if (modalElement) {
        observer.observe(modalElement, {
          attributes: true,
          attributeFilter: ['style']
        });
        updateScrollLock(); // Initial check
      } else {
        // If modal doesn't exist yet, try again shortly
        setTimeout(startObserver, 100);
      }
    };

    startObserver();

    // Cleanup
    return () => {
      observer.disconnect();
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, []);

  return (
    <CartContext.Provider value={{ quantity, setQuantity, openEmbedModal }}>
      {children}
    </CartContext.Provider>
  );
};
